import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  alpha,
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { AutoAwesome as SparkleIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { RelativeTime } from '../components/RelativeTime';
import { SectionTitle, StatCard, fmtNum, fmtPct } from '../components/performance/common';
import { adminBuildGuideApi, BuildGuideFunnel, BuildKind, FunnelConversation, FunnelStep, FunnelStepKey } from '../services/adminBuildGuideApi';
import { usePageTitle } from '../utils/pageTitle';
import { usePersistentState } from '../utils/usePersistentState';

const WINDOWS = [7, 30, 90];

const STEP_COLORS: Record<FunnelStepKey, string> = {
  started: '#1976d2',
  recommended: '#7b1fa2',
  clicked: '#0288d1',
  built: '#2e7d32',
  ran: '#ed6c02',
};

const BUILD_LABELS: Record<BuildKind, [string, string]> = {
  functions: ['function', 'functions'],
  chatbots: ['chatbot', 'chatbots'],
  chains: ['chain', 'chains'],
  objects: ['object', 'objects'],
};

const fmtDay = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const builtSummary = (counts: Record<BuildKind, number>) =>
  (Object.keys(BUILD_LABELS) as BuildKind[])
    .filter((k) => counts[k] > 0)
    .map((k) => `${counts[k]} ${BUILD_LABELS[k][counts[k] === 1 ? 0 : 1]}`)
    .join(', ');

const FunnelBars: React.FC<{ steps: FunnelStep[] }> = ({ steps }) => (
  <Stack spacing={1.5}>
    {steps.map((s, i) => (
      <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ width: { xs: 120, sm: 190 }, flexShrink: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {s.label}
          </Typography>
          {i > 0 && (
            <Typography variant="caption" color="text.secondary">
              {fmtPct(s.of_previous, 0)} of previous step
            </Typography>
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, position: 'relative', height: 30, borderRadius: 1, bgcolor: 'action.hover', overflow: 'hidden' }}>
          <Box
            sx={{
              height: '100%',
              width: `${Math.max((s.share ?? 0) * 100, s.count > 0 ? 1 : 0)}%`,
              bgcolor: alpha(STEP_COLORS[s.key], 0.85),
              transition: 'width 0.3s',
            }}
          />
        </Box>
        <Box sx={{ width: 110, flexShrink: 0, textAlign: 'right' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {fmtNum(s.count)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {fmtPct(s.share)} of conversations
          </Typography>
        </Box>
      </Box>
    ))}
  </Stack>
);

const YesNo: React.FC<{ yes: boolean; open: boolean; tooltip?: string; days: number }> = ({ yes, open, tooltip, days }) => {
  if (yes) {
    return (
      <Tooltip title={tooltip || ''} disableHoverListener={!tooltip}>
        <Chip size="small" color="success" label="Yes" />
      </Tooltip>
    );
  }
  if (open) {
    return (
      <Tooltip title={`Started less than ${days} days ago, so this can still change`}>
        <Chip size="small" variant="outlined" label="Not yet" />
      </Tooltip>
    );
  }
  return <Chip size="small" variant="outlined" label="No" />;
};

const BreakdownTable: React.FC<{ head: string; shareHead: string; rows: { key: string; label: string; conversations: number; share: number | null }[] }> = ({
  head,
  shareHead,
  rows,
}) => (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{head}</TableCell>
          <TableCell align="right">Conversations</TableCell>
          <TableCell align="right">{shareHead}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={3} align="center">
              Nothing yet
            </TableCell>
          </TableRow>
        ) : (
          rows.map((r) => (
            <TableRow key={r.key}>
              <TableCell>{r.label}</TableCell>
              <TableCell align="right">{fmtNum(r.conversations)}</TableCell>
              <TableCell align="right">{fmtPct(r.share)}</TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

const RecentTable: React.FC<{ rows: FunnelConversation[]; data: BuildGuideFunnel }> = ({ rows, data }) => {
  const days = data.window.attribution_days;
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Account</TableCell>
            <TableCell>Started</TableCell>
            <TableCell align="right">Turns</TableCell>
            <TableCell>Recommended</TableCell>
            <TableCell>Clicked</TableCell>
            <TableCell>Built</TableCell>
            <TableCell>Ran from code</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.conversation_id} hover>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {r.customer_tag || r.customer_id}
                </Typography>
              </TableCell>
              <TableCell>
                <RelativeTime value={r.started_at} />
              </TableCell>
              <TableCell align="right">{r.turns || '–'}</TableCell>
              <TableCell>
                {r.kind ? (
                  <Chip size="small" variant="outlined" color="secondary" label={data.labels.kinds[r.kind] || r.kind} />
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    –
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                {r.targets.length ? (
                  <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                    {r.targets.map((t) => (
                      <Chip key={t} size="small" variant="outlined" color="primary" label={data.labels.targets[t] || t} />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    –
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <YesNo yes={r.built} open={r.open} days={days} tooltip={builtSummary(r.built_counts)} />
              </TableCell>
              <TableCell>
                <YesNo yes={r.ran} open={r.open} days={days} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const AdminBuildGuideFunnel: React.FC = () => {
  usePageTitle('Build Guide funnel');
  const { getIdToken } = useAuth();
  const api = useMemo(() => adminBuildGuideApi(getIdToken), [getIdToken]);
  const [days, setDays] = usePersistentState('buildGuideFunnel.days', 30, WINDOWS);
  const [includeAdmins, setIncludeAdmins] = usePersistentState<boolean>('buildGuideFunnel.includeAdmins', false);
  const [data, setData] = useState<BuildGuideFunnel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .funnel(days, includeAdmins)
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [api, days, includeAdmins, reload]);

  const step = (key: FunnelStepKey) => data?.steps.find((s) => s.key === key);
  const attributionDays = data?.window.attribution_days ?? 7;
  const daily = useMemo(() => (data?.daily ?? []).map((d) => ({ ...d, label: fmtDay(d.date) })), [data]);
  const empty = !!data && data.totals.conversations === 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4">Build Guide funnel</Typography>
          <Typography variant="body2" color="text.secondary">
            Conversations with the Build Guide, and whether they led to a recommendation, a click, something built and a run from code.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Tooltip title="Admin accounts and the shared public account are left out by default. Impersonated sessions are never recorded.">
            <FormControlLabel
              control={<Switch size="small" checked={includeAdmins} onChange={(e) => setIncludeAdmins(e.target.checked)} />}
              label="Include admin accounts"
            />
          </Tooltip>
          <ToggleButtonGroup size="small" exclusive value={days} onChange={(_, v) => v && setDays(v)}>
            {WINDOWS.map((d) => (
              <ToggleButton key={d} value={d}>
                {d}d
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <IconButton onClick={() => setReload((r) => r + 1)} disabled={loading}>
            {loading ? <CircularProgress size={22} /> : <RefreshIcon />}
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {data?.truncated && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Too many events to read them all; the oldest conversations in this window may be incomplete. Pick a shorter window.
        </Alert>
      )}

      {empty && (
        <Paper variant="outlined" sx={{ p: { xs: 3, sm: 6 }, textAlign: 'center' }}>
          <SparkleIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
          <Typography variant="h6">No Build Guide conversations in the last {days} days</Typography>
          <Typography variant="body2" color="text.secondary">
            Conversations show up here after their first reply. Try a longer window{includeAdmins ? '' : ' or include admin accounts'}.
          </Typography>
        </Paper>
      )}

      {data && !empty && (
        <Box sx={{ opacity: loading ? 0.6 : 1 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 1.5, mb: 3 }}>
            <StatCard
              label="Conversations"
              value={fmtNum(data.totals.conversations)}
              caption={data.totals.open_conversations ? `${fmtNum(data.totals.open_conversations)} under ${attributionDays} days old` : undefined}
              tooltip="Conversations that got at least one reply, started in this window"
            />
            <StatCard label="Accounts" value={fmtNum(data.totals.accounts)} />
            <StatCard label="Avg turns" value={data.totals.avg_turns ?? '–'} tooltip="Messages the user sent per conversation" />
            <StatCard label="Recommendation rate" value={fmtPct(step('recommended')?.share)} />
            <StatCard label="Click rate" value={fmtPct(step('clicked')?.share)} tooltip="Clicked at least one button or link in a reply" />
            <StatCard
              label="Build rate"
              value={fmtPct(step('built')?.share)}
              tooltip={`The account created a new function, chatbot, chain or output object within ${attributionDays} days after the conversation started`}
            />
            <StatCard
              label="Ran from code"
              value={fmtPct(step('ran')?.share)}
              tooltip={`The account ran a function or chain with an API key within ${attributionDays} days after the conversation started`}
            />
          </Box>

          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <SectionTitle
              title="Funnel"
              help={`Built and ran from code count what the account did within ${attributionDays} days after the conversation started, whether or not it clicked through. Two conversations close together both get credit for the same build.`}
            />
            <Box sx={{ mt: 2 }}>
              <FunnelBars steps={data.steps} />
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <SectionTitle title="By day" help="By the day the conversation started (UTC)" />
            <Box sx={{ height: 240 }}>
              <ResponsiveContainer>
                <ComposedChart data={daily} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={12} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <ChartTooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="conversations" name="Conversations" fill={alpha(STEP_COLORS.started, 0.6)} />
                  <Line dataKey="recommended" name="Recommended" stroke={STEP_COLORS.recommended} dot={false} isAnimationActive={false} />
                  <Line dataKey="clicked" name="Clicked" stroke={STEP_COLORS.clicked} dot={false} isAnimationActive={false} />
                  <Line dataKey="built" name="Built" stroke={STEP_COLORS.built} dot={false} isAnimationActive={false} />
                  <Line dataKey="ran" name="Ran from code" stroke={STEP_COLORS.ran} dot={false} isAnimationActive={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <SectionTitle title="Recommendations" help="The latest recommendation in each conversation" />
              <BreakdownTable
                head="Recommended"
                shareHead="Of recommended"
                rows={data.recommendation_kinds.map((r) => ({ key: r.kind, label: r.label, conversations: r.conversations, share: r.share }))}
              />
            </Paper>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <SectionTitle title="Click destinations" help="Conversations that clicked through to each page" />
              <BreakdownTable
                head="Destination"
                shareHead="Of clicked"
                rows={data.click_targets.map((r) => ({ key: r.target, label: r.label, conversations: r.conversations, share: r.share }))}
              />
            </Paper>
          </Box>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <SectionTitle title="Recent conversations" help={`The latest ${data.recent.length} in this window. Message text is never stored.`} />
            <RecentTable rows={data.recent} data={data} />
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default AdminBuildGuideFunnel;
